import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import validator from 'validator'

import authService from '../../../services/authService'

import { getPasswordValidationError } from '../../../utils/passwordUtils'

import './ChangePasswordForm.css'
import CTextField from '../../../components/UI/CTextField'
import CButton from '../../../components/UI/CButton'

/**
 * Reusable component for changing a user's password
 */
const ChangePasswordForm = ({
    onClose,
    onSuccess,
    onError,
}) => {
    const dispatch = useDispatch()
    const { user } = useSelector((state) => state.auth)
    const [loading, setLoading] = useState(false)

    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    })
    const [validationErrors, setValidationErrors] = useState({})
    const [showPasswords, setShowPasswords] = useState({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
    })

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }))

        setValidationErrors(prev => {
            const newErrors = { ...prev }

            if (field === 'newPassword') {
                const passwordError = getPasswordValidationError(value)
                if (passwordError) {
                    newErrors.newPassword = passwordError
                } else if (value && value === formData.currentPassword) {
                    newErrors.newPassword = 'New password must be different from current password'
                } else {
                    delete newErrors.newPassword
                }
                // Re-validate confirm password when new password changes
                if (formData.confirmPassword) {
                    if (value !== formData.confirmPassword) {
                        newErrors.confirmPassword = 'Passwords do not match'
                    } else {
                        delete newErrors.confirmPassword
                    }
                }
            } else if (field === 'currentPassword') {
                delete newErrors.currentPassword
                // Re-validate new password against updated current password
                if (formData.newPassword && value && formData.newPassword === value) {
                    newErrors.newPassword = 'New password must be different from current password'
                } else if (newErrors.newPassword === 'New password must be different from current password') {
                    delete newErrors.newPassword
                }
            } else if (field === 'confirmPassword') {
                if (!value) {
                    delete newErrors.confirmPassword
                } else if (formData.newPassword !== value) {
                    newErrors.confirmPassword = 'Passwords do not match'
                } else {
                    delete newErrors.confirmPassword
                }
            } else {
                delete newErrors[field]
            }

            return newErrors
        })
    }

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }))
    }

    const validateForm = () => {
        const errors = {}

        if (validator.isEmpty(formData.currentPassword.trim())) {
            errors.currentPassword = 'Current password is required'
        }

        if (validator.isEmpty(formData.newPassword.trim())) {
            errors.newPassword = 'New password is required'
        } else if (formData.newPassword.length < 8) {
            errors.newPassword = 'Password must be at least 8 characters'
        } else {
            // Check for uppercase letter
            if (!/[A-Z]/.test(formData.newPassword)) {
                errors.newPassword = 'Password must include at least one uppercase letter'
            }
            // Check for lowercase letter
            else if (!/[a-z]/.test(formData.newPassword)) {
                errors.newPassword = 'Password must include at least one lowercase letter'
            }
            // Check for number
            else if (!/[0-9]/.test(formData.newPassword)) {
                errors.newPassword = 'Password must include at least one number'
            }
            // Check for special character
            else if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword)) {
                errors.newPassword = 'Password must include at least one special character'
            }
        }

        if (validator.isEmpty(formData.confirmPassword.trim())) {
            errors.confirmPassword = 'Please confirm your new password'
        } else if (formData.newPassword !== formData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match'
        }

        if (formData.currentPassword === formData.newPassword && !validator.isEmpty(formData.currentPassword)) {
            errors.newPassword = 'New password must be different from current password'
        }

        setValidationErrors(errors)
        return Object.keys(errors).length === 0
    }

    const isFormValid = () => {
        // Check if all fields have values
        if (!formData.currentPassword.trim() || !formData.newPassword.trim() || !formData.confirmPassword.trim()) {
            return false
        }

        // Check password requirements
        if (formData.newPassword.length < 8) {
            return false
        }

        if (!/[A-Z]/.test(formData.newPassword)) {
            return false
        }

        if (!/[a-z]/.test(formData.newPassword)) {
            return false
        }

        if (!/[0-9]/.test(formData.newPassword)) {
            return false
        }

        if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword)) {
            return false
        }

        // Check passwords match
        if (formData.newPassword !== formData.confirmPassword) {
            return false
        }

        // Check new password is different from current
        if (formData.currentPassword === formData.newPassword) {
            return false
        }

        return true
    }

    const handleCancel = () => {
        setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' })
        setValidationErrors({})
        if (onClose) onClose()
    }

    const handleSubmit = async () => {
        if (!validateForm()) {
            return
        }

        setLoading(true)

        try {
            await authService.changePassword(
                user.userid,
                formData.currentPassword.trim(),
                formData.newPassword.trim(),
                {
                    showSnackbar: true,
                    showSuccessSnackbar: true,
                    successMessage: 'Password changed successfully',
                    errorMessage: 'Failed to change password'
                }
            )

            // Reset form
            setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' })
            setValidationErrors({})

            if (onSuccess) onSuccess()
            if (onClose) onClose()
        } catch (err) {
            console.error('Error changing password:', err)
            if (onError) onError(err)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="change-password-form">
            <span className="form-description">Keep your account secure by updating your password below.</span>
            <CTextField
                label="Current Password"
                type={showPasswords.currentPassword ? "text" : "password"}
                value={formData.currentPassword}
                onChange={(e) => handleFieldChange('currentPassword', e.target.value)}
                error={!!validationErrors.currentPassword}
                helperText={validationErrors.currentPassword}
                leftIcon="lock"
                rightIcon={showPasswords.currentPassword ? "hide-password" : "reveal-password"}
                onRightIconClick={() => togglePasswordVisibility('currentPassword')}
                fullWidth
                required
            />

            <CTextField
                label="New Password"
                type={showPasswords.newPassword ? "text" : "password"}
                value={formData.newPassword}
                onChange={(e) => handleFieldChange('newPassword', e.target.value)}
                error={!!validationErrors.newPassword}
                helperText={validationErrors.newPassword}
                leftIcon="lock"
                rightIcon={showPasswords.newPassword ? "hide-password" : "reveal-password"}
                onRightIconClick={() => togglePasswordVisibility('newPassword')}
                fullWidth
                required
            />

            <CTextField
                label="Confirm New Password"
                type={showPasswords.confirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
                error={!!validationErrors.confirmPassword}
                helperText={validationErrors.confirmPassword}
                leftIcon="lock"
                rightIcon={showPasswords.confirmPassword ? "hide-password" : "reveal-password"}
                onRightIconClick={() => togglePasswordVisibility('confirmPassword')}
                fullWidth
                required
            />

            <div className="password-requirements">
                <span className="header-5 title">Password requirements:</span>
                <ul>
                    <li className="text-5">Password must be at least 8 characters</li>
                    <li className="text-5">Passwords must have at least one uppercase letter, one lowercase letter, one number, and one special character</li>
                </ul>
            </div>

            <div className="form-actions">
                <CButton
                    variant="outlined"
                    size="large"
                    onClick={handleCancel}
                    colorTheme="primary"
                    label="Cancel"
                    disabled={loading}
                />
                <CButton
                    variant="contained"
                    size="large"
                    onClick={handleSubmit}
                    colorTheme="primary"
                    label={'Update Password'}
                    showLoader={loading}
                    disabled={loading || !isFormValid()}
                />
            </div>

            <div className="form-footer">
                <span className="text-5">Having trouble? We're here to help <a className='header-4' href="mailto:info@awardsapp.co.za">contact support</a>.</span>
            </div>
        </div>
    )
}

export default ChangePasswordForm
